import { Splide } from '@hh/global.js'
import {
  isScrollTargetElementVisible,
  scrollPageToElementWithStickyStackOffset,
} from '@hh/component-anchor-target-utils.js'

/** Splide track horizontal padding: 16px (mobile) / 24px (desktop, min-width 990px) - same as anchor-carousel-atelier.js. */
function sideTrackPaddingPx() {
  return window.innerWidth < 990 ? 16 : 24
}

/**
 * `sections/anchor-carousel.liquid`: desktop static row (CSS-only hover reveal for the
 * "Explore" CTA) + mobile Splide carousel, where the CTA instead follows whichever card
 * is currently centered via the `.in-view` class (mirrors `anchor-carousel-atelier.js`'s
 * `syncInViewFromSplide`). The Splide options/resize handling below are copied verbatim
 * from `AnchorAtelierSplideCarousel` so the slide/drag feel matches exactly.
 */
class AnchorCarousel extends HTMLElement {
  connectedCallback() {
    this.initClickToScroll()
    this.initSlider()

    this._resizeT = null
    this._onWindowResize = () => {
      clearTimeout(this._resizeT)
      this._resizeT = setTimeout(() => {
        this._resizeT = null
        this.mountOrTeardownSplide()
        this.applyBasePadding()
        this.syncInView()
      }, 150)
    }
    window.addEventListener('resize', this._onWindowResize)
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onWindowResize)
    clearTimeout(this._resizeT)
    this._resizeT = null
    this.splide?.destroy()
    this.splide = null
  }

  initClickToScroll() {
    this.addEventListener('click', (event) => {
      const card = event.target.closest('[data-anchor-carousel-card]')
      if (!card) {
        return
      }
      // Strip the leading `#` and use `getElementById` rather than `querySelector` on
      // the raw string - a collection handle that starts with a digit (or contains
      // other CSS-special characters) is a syntactically invalid ID selector and would
      // make `querySelector` throw instead of just failing to match.
      const targetId = card.getAttribute('data-scroll-target')?.trim().replace(/^#/, '')
      const target = targetId ? document.getElementById(targetId) : null
      // `getElementById` matches regardless of visibility - a Subcollection section
      // gets `hidden` when a filter/non-default sort is applied (sections/subcollection.
      // liquid), and scrolling to it then would land on a collapsed, invisible target.
      if (!target || !isScrollTargetElementVisible(target)) {
        return
      }
      event.preventDefault()
      scrollPageToElementWithStickyStackOffset(target)
    })
  }

  initSlider() {
    const root = this.querySelector('[data-anchor-carousel-splide]')
    if (!root) {
      return
    }
    this._splideRoot = root
    // ≤5-card branch: the mobile-only instance sits `display: none` from 1000px (the
    // static row takes over) - CSS alone. Mounting Splide against a hidden/zero-width
    // track breaks its slide-width math, and a later `refresh()` doesn't reliably
    // recover, so it's not mounted at all while hidden (matches
    // `AnchorAtelierSplideCarousel`, which defers/tears down the same way).
    this._isMobileOnly = root.hasAttribute('data-anchor-mobile-only')
    this.mountOrTeardownSplide()
  }

  mountOrTeardownSplide() {
    const root = this._splideRoot
    if (!root) {
      return
    }
    const shouldBeMounted = !this._isMobileOnly || window.innerWidth < 1000

    if (shouldBeMounted && !this.splide) {
      /** 1.5 slides on narrow viewports; 5.5 on desktop for the >5-card (always-slider) case. Few-card carousels (`data-anchor-mobile-only`) stay at 1.5 - desktop uses the static row instead. */
      const perPageOptions = {
        perPage: 1.5,
        ...(this._isMobileOnly
          ? {}
          : {
              breakpoints: {
                990: {
                  perPage: 5.5,
                  focus: 0,
                },
              },
            }),
      }

      this.splide = new Splide(root, {
        type: 'slide',
        mediaQuery: 'min',
        focus: 'center',
        perMove: 1,
        gap: '12px',
        padding: {
          left: sideTrackPaddingPx(),
          right: sideTrackPaddingPx(),
        },
        arrows: false,
        pagination: false,
        speed: 400,
        ...perPageOptions,
      })

      // `moved` fires before Splide has finished flagging the new slide `.is-active` -
      // reading it synchronously here lags by one move (matches AnchorAtelierSplideCarousel's
      // `refreshUi()`, which defers the same way).
      const scheduleSyncInView = () => requestAnimationFrame(() => this.syncInView())
      this.splide.on('mounted moved', scheduleSyncInView)
      this.splide.mount()
      scheduleSyncInView()
    } else if (!shouldBeMounted && this.splide) {
      this.splide.destroy()
      this.splide = null
    }
  }

  applyBasePadding() {
    if (!this.splide) {
      return
    }
    const p = sideTrackPaddingPx()
    // Do not spread `this.splide.options` - it flattens breakpoint-merged values.
    this.splide.options = { padding: { left: p, right: p } }
    this.splide.refresh()
  }

  syncInView() {
    const list = this.splide?.root?.querySelector('.splide__list')
    if (!list) {
      return
    }
    for (const li of list.querySelectorAll(':scope > .splide__slide')) {
      const card = li.querySelector('[data-anchor-carousel-card]')
      card?.classList.toggle('in-view', li.classList.contains('is-active'))
    }
  }
}

customElements.define('anchor-carousel', AnchorCarousel)
